export type RabbitHoleLanguageSignature =
  | "none"
  | "corner-lines"
  | "baseline-ticks"
  | "arc-line"
  | "dotted-line";

export type RabbitHoleEditionDesign = {
  familyId: string;
  variationId: string;
  paper: string;
  ink: string;
  primaryAccent: string;
  secondaryAccent: string;
  textureIndex: number;
  gradientX: number;
  gradientY: number;
  gradientSize: number;
  gradientOpacity: number;
  spineWidth: number;
  spineTone: string;
  ruleStyle: "single" | "double" | "offset";
  signature: RabbitHoleLanguageSignature;
  signatureOffset: number;
  signatureOpacity: number;
};

export type RabbitHoleCoverTone = "active" | "read" | "inactive";

export type RabbitHoleCoverPalette = {
  paper: string;
  ink: string;
  primaryAccent: string;
  secondaryAccent: string;
  spineTone: string;
};

type EditionDesignInput = {
  language: string;
  topic: string;
  weekStart: string;
  dayIndex?: number | null;
};

type TitleLayoutOptions = {
  variant?: "full" | "compact" | "mini";
  maxLines?: number;
  forceEditorialPair?: boolean;
};

export type EditionTitleLayout = {
  lines: string[];
  longestLineWeight: number;
  size: "xl" | "lg" | "md" | "sm";
};

const PALETTES = [
  { paper: "#f2ebe2", ink: "#171514", primary: "#de4b34", secondary: "#f29b76", spine: "#d7c9ba" },
  { paper: "#eee9df", ink: "#161818", primary: "#1f6680", secondary: "#75a9b8", spine: "#d2c9bb" },
  { paper: "#f1ebdf", ink: "#181610", primary: "#b47c22", secondary: "#e2b968", spine: "#d8cbbb" },
  { paper: "#ece9e0", ink: "#141716", primary: "#3c745c", secondary: "#8bae87", spine: "#d1cabd" },
  { paper: "#f0e7e5", ink: "#191417", primary: "#994b69", secondary: "#d995aa", spine: "#d5c6c4" },
  { paper: "#ebe9e4", ink: "#131518", primary: "#505a94", secondary: "#9299c3", spine: "#cec9c0" },
  { paper: "#f0e9dc", ink: "#181411", primary: "#ad5c2b", secondary: "#dd9a64", spine: "#d6c8b5" },
  { paper: "#eceae2", ink: "#141715", primary: "#5d7040", secondary: "#a4ad72", spine: "#cfccbf" },
] as const;

const LANGUAGE_SIGNATURES: Record<string, RabbitHoleLanguageSignature> = {
  ENGLISH: "none",
  FRENCH: "corner-lines",
  GERMAN: "baseline-ticks",
  ITALIAN: "arc-line",
};

// The edition family may vary by topic, but the paper color is a stable cue for
// the learner's language space. Keep this aligned with the overview previews.
const LANGUAGE_PALETTE_INDEX: Record<string, number> = {
  ENGLISH: 3,
  FRENCH: 0,
  GERMAN: 2,
  ITALIAN: 5,
};

const TEXTURE_LAYERS = [
  "repeating-linear-gradient(94deg, transparent 0 6px, rgba(23,21,20,.025) 7px 8px)",
  "radial-gradient(circle at 20% 18%, rgba(255,255,255,.3) 0 1px, transparent 1.5px)",
  "repeating-linear-gradient(2deg, rgba(23,21,20,.018) 0 1px, transparent 1px 5px)",
  "radial-gradient(ellipse at 76% 24%, rgba(255,255,255,.26) 0 1px, transparent 2px)",
  "repeating-linear-gradient(124deg, transparent 0 9px, rgba(23,21,20,.022) 10px 11px)",
  "radial-gradient(circle at 42% 62%, rgba(23,21,20,.022) 0 .8px, transparent 1.4px)",
] as const;

function withAlpha(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function toGrayscaleHex(hex: string): string {
  const normalized = hex.replace("#", "");
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const gray = Math.round(red * 0.2126 + green * 0.7152 + blue * 0.0722);
  const channel = gray.toString(16).padStart(2, "0");
  return `#${channel}${channel}${channel}`;
}

function mixHexColors(a: string, b: string, weight: number): string {
  const normalizedA = a.replace("#", "");
  const normalizedB = b.replace("#", "");
  const clampWeight = Math.max(0, Math.min(1, weight));
  const mixChannel = (index: number) => {
    const aValue = parseInt(normalizedA.slice(index, index + 2), 16);
    const bValue = parseInt(normalizedB.slice(index, index + 2), 16);
    return Math.round(aValue * clampWeight + bValue * (1 - clampWeight))
      .toString(16)
      .padStart(2, "0");
  };

  return `#${mixChannel(0)}${mixChannel(2)}${mixChannel(4)}`;
}

export function resolveRabbitHoleCoverPalette(
  design: RabbitHoleEditionDesign,
  tone: RabbitHoleCoverTone = "active",
): RabbitHoleCoverPalette {
  const palette = {
    paper: design.paper,
    ink: design.ink,
    primaryAccent: design.primaryAccent,
    secondaryAccent: design.secondaryAccent,
    spineTone: design.spineTone,
  };

  if (tone === "active") return palette;
  if (tone === "read") {
    const mutedPaper = mixHexColors(palette.paper, toGrayscaleHex(palette.paper), 0.62);
    const mutedInk = mixHexColors(palette.ink, toGrayscaleHex(palette.ink), 0.58);
    const mutedPrimary = mixHexColors(
      palette.primaryAccent,
      toGrayscaleHex(palette.primaryAccent),
      0.52,
    );
    const mutedSecondary = mixHexColors(
      palette.secondaryAccent,
      toGrayscaleHex(palette.secondaryAccent),
      0.52,
    );
    const mutedSpine = mixHexColors(
      palette.spineTone,
      toGrayscaleHex(palette.spineTone),
      0.68,
    );

    return {
      paper: mutedPaper,
      ink: mutedInk,
      primaryAccent: mutedPrimary,
      secondaryAccent: mutedSecondary,
      spineTone: mutedSpine,
    };
  }

  return {
    paper: toGrayscaleHex(palette.paper),
    ink: toGrayscaleHex(palette.ink),
    primaryAccent: toGrayscaleHex(palette.primaryAccent),
    secondaryAccent: toGrayscaleHex(palette.secondaryAccent),
    spineTone: toGrayscaleHex(palette.spineTone),
  };
}

export function createRabbitHoleCoverBackground(
  design: RabbitHoleEditionDesign,
  tone: RabbitHoleCoverTone = "active",
): string {
  const texture = TEXTURE_LAYERS[design.textureIndex] ?? TEXTURE_LAYERS[0];
  const palette = resolveRabbitHoleCoverPalette(design, tone);
  const intensity = tone === "inactive" ? 0.34 : tone === "read" ? 0.72 : 1;
  const primaryOpacity = design.gradientOpacity * intensity;
  const secondaryOpacity = design.gradientOpacity * 0.68 * intensity;
  const footerOpacity = tone === "inactive" ? 0.1 : tone === "read" ? 0.18 : 0.24;
  return [
    texture,
    `radial-gradient(${design.gradientSize}% ${Math.round(design.gradientSize * 0.82)}% at ${design.gradientX}% ${design.gradientY}%, ${withAlpha(palette.primaryAccent, primaryOpacity)} 0%, ${withAlpha(palette.secondaryAccent, secondaryOpacity)} 46%, transparent 78%)`,
    `linear-gradient(168deg, ${palette.paper} 0%, ${palette.paper} 54%, ${withAlpha(palette.secondaryAccent, footerOpacity)} 100%)`,
  ].join(", ");
}

function normalizeSeedPart(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US");
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function choose<T>(values: readonly T[], random: () => number): T {
  return values[Math.floor(random() * values.length)] ?? values[0];
}

export function createRabbitHoleEditionDesign({
  language,
  topic,
  weekStart,
  dayIndex = 0,
}: EditionDesignInput): RabbitHoleEditionDesign {
  const normalizedLanguage = language.trim().toUpperCase();
  const familySeedText = `${normalizedLanguage}|${normalizeSeedPart(topic)}|${weekStart.trim()}`;
  const familySeed = hashString(familySeedText);
  const familyRandom = createSeededRandom(familySeed);
  const safeDayIndex = Math.max(0, Math.min(6, dayIndex ?? 0));
  const daySeed = hashString(`${familySeedText}|day:${safeDayIndex}`);
  const dayRandom = createSeededRandom(daySeed);
  const paletteIndex = LANGUAGE_PALETTE_INDEX[normalizedLanguage] ?? familySeed % PALETTES.length;
  const palette = PALETTES[paletteIndex] ?? PALETTES[0];

  return {
    familyId: familySeed.toString(36),
    variationId: daySeed.toString(36),
    paper: palette.paper,
    ink: palette.ink,
    primaryAccent: palette.primary,
    secondaryAccent: palette.secondary,
    textureIndex: Math.floor(familyRandom() * 6),
    gradientX: Math.round(54 + dayRandom() * 34),
    gradientY: Math.round(75 + dayRandom() * 18),
    gradientSize: Math.round(58 + familyRandom() * 24),
    gradientOpacity: Number((0.72 + dayRandom() * 0.18).toFixed(2)),
    spineWidth: choose([7, 9, 11, 13], familyRandom),
    spineTone: palette.spine,
    ruleStyle: choose(["single", "double", "offset"] as const, familyRandom),
    signature: LANGUAGE_SIGNATURES[normalizedLanguage] ?? "dotted-line",
    signatureOffset: Math.round(8 + dayRandom() * 12),
    signatureOpacity: Number((0.12 + familyRandom() * 0.08).toFixed(2)),
  };
}

function characterWeight(character: string): number {
  if (/\s/u.test(character)) return 0.35;
  if (/[ilIjtfr1'’.:,;]/u.test(character)) return 0.5;
  if (/[mwMW@%&]/u.test(character)) return 1.35;
  if (/\p{Lu}/u.test(character)) return 1.08;
  return 0.92;
}

export function estimateEditionTextWidth(value: string): number {
  return Array.from(value).reduce(
    (total, character) => total + characterWeight(character),
    0,
  );
}

function combinationsForLineCount(words: string[], lineCount: number): string[][] {
  if (lineCount === 1) return [[words.join(" ")]];
  const combinations: string[][] = [];

  const visit = (start: number, remainingLines: number, lines: string[]) => {
    if (remainingLines === 1) {
      combinations.push([...lines, words.slice(start).join(" ")]);
      return;
    }
    const lastBreak = words.length - remainingLines + 1;
    for (let end = start + 1; end <= lastBreak; end += 1) {
      visit(end, remainingLines - 1, [...lines, words.slice(start, end).join(" ")]);
    }
  };

  visit(0, lineCount, []);
  return combinations;
}

function layoutScore(lines: string[]): number {
  const weights = lines.map(estimateEditionTextWidth);
  const average = weights.reduce((sum, weight) => sum + weight, 0) / weights.length;
  const variance = weights.reduce(
    (sum, weight) => sum + (weight - average) ** 2,
    0,
  );
  const longest = Math.max(...weights);
  const shortest = Math.min(...weights);
  const finalWords = lines.at(-1)?.split(" ") ?? [];
  const orphanPenalty =
    lines.length > 1 && finalWords.length === 1 && weights.at(-1)! < average * 0.62
      ? 140
      : 0;
  const imbalancePenalty = longest > shortest * 2.35 ? 70 : 0;
  return variance + longest * 0.45 + orphanPenalty + imbalancePenalty;
}

export function splitEditionTitle(
  value: string,
  options: TitleLayoutOptions = {},
): EditionTitleLayout {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized) {
    return { lines: [""], longestLineWeight: 0, size: "lg" };
  }

  const variant = options.variant ?? "full";
  const words = normalized.split(" ");
  const totalWeight = estimateEditionTextWidth(normalized);
  const forceEditorialPair =
    (options.forceEditorialPair ?? true) &&
    words.length === 2 &&
    totalWeight >= 6 &&
    variant !== "mini";
  const automaticLineCount = forceEditorialPair
    ? 2
    : totalWeight <= (variant === "full" ? 14 : variant === "compact" ? 11 : 9)
      ? 1
      : totalWeight <= (variant === "full" ? 28 : variant === "compact" ? 22 : 17)
        ? 2
        : totalWeight <= (variant === "full" ? 46 : variant === "compact" ? 35 : 27)
          ? 3
          : 4;
  const maxLines = Math.max(1, Math.min(options.maxLines ?? 4, words.length));
  const lineCount = Math.min(automaticLineCount, maxLines);
  const candidates = combinationsForLineCount(words, lineCount);
  const lines = candidates.reduce((best, candidate) =>
    layoutScore(candidate) < layoutScore(best) ? candidate : best,
  );
  const longestLineWeight = Math.max(...lines.map(estimateEditionTextWidth));
  const size: EditionTitleLayout["size"] =
    variant === "compact"
      ? lines.length <= 2 && longestLineWeight < 9
        ? "xl"
        : lines.length <= 3 && longestLineWeight < 11
          ? "lg"
          : lines.length <= 3 && longestLineWeight < 15
            ? "md"
            : "sm"
      : variant === "mini"
        ? lines.length <= 2 && longestLineWeight < 8
          ? "xl"
          : lines.length <= 2 && longestLineWeight < 11
            ? "lg"
            : lines.length <= 3 && longestLineWeight < 15
              ? "md"
              : "sm"
        : lines.length <= 2 && longestLineWeight < 17
          ? "xl"
          : lines.length <= 3 && longestLineWeight < 20
            ? "lg"
            : lines.length <= 3 && longestLineWeight < 27
              ? "md"
              : "sm";

  return { lines, longestLineWeight, size };
}

export function getContrastRatio(foreground: string, background: string): number {
  const luminance = (hex: string) => {
    const channels = hex
      .replace("#", "")
      .match(/.{2}/g)
      ?.map((channel) => parseInt(channel, 16) / 255) ?? [0, 0, 0];
    const converted = channels.map((channel) =>
      channel <= 0.03928
        ? channel / 12.92
        : ((channel + 0.055) / 1.055) ** 2.4,
    );
    return converted[0] * 0.2126 + converted[1] * 0.7152 + converted[2] * 0.0722;
  };
  const first = luminance(foreground);
  const second = luminance(background);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

export function formatRabbitHoleEditionDateRange(
  weekStart: string,
  weekEnd: string,
  appLanguage: string,
): string {
  const locale = appLanguage === "GERMAN" ? "de-DE" : "en-US";
  const formatter = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  });
  const start = new Date(`${weekStart}T00:00:00`);
  const end = new Date(`${weekEnd}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "";
  }
  if (weekStart === weekEnd) {
    return formatter.format(start);
  }
  return `${formatter.format(start)}–${formatter.format(end)}`;
}

export function getRabbitHoleEditionTitle(topic: string | null | undefined): {
  title: string;
  pending: boolean;
} {
  const normalized = (topic ?? "").trim();
  if (!normalized) return { title: "", pending: false };
  if (normalized.toLocaleLowerCase("en-US").startsWith("rabbit hole")) {
    return { title: "", pending: true };
  }
  return { title: normalized, pending: false };
}
